import { ChangeEvent, FunctionComponent, useCallback, useMemo } from 'react';
import { findIndex, sortBy } from 'es-toolkit/compat';
import clsx from 'clsx';

import { OptionImage } from 'components/profile/quiz/OptionImage';
import { Typography } from 'omakase';
import { consumer } from '@tock/coretypes';
import { defaultMemoize } from 'reselect';
import { useMountEffect } from '@tock/hooks';
import Slider from 'omakase/Slider/Slider';

export interface Props {
  question: consumer.IPatronQuizQuestion;
  selectedOptionId?: number;
  onAnswer(optionId: number[]): void;
}

const onAnswerMemo = defaultMemoize((answerId: number, onAnswer: (optionId: number[]) => void) => {
  onAnswer([answerId]);
});

const SliderQuizQuestion: FunctionComponent<Props> = ({ question, selectedOptionId, onAnswer }) => {
  useMountEffect(() => {
    if (!selectedOptionId) {
      question.option && question.option[0].id && onAnswer([question.option[0].id]);
    }
  });

  const onAnswerInternalMemo = useCallback(onAnswer, [onAnswer]);

  const sortedOptions = useMemo(() => sortBy(question.option, 'sortOrder'), [question.option]);
  const selectedOptionIndex = Math.max(
    findIndex(sortedOptions, (option) => option.id === selectedOptionId),
    0
  );

  if (!question.option) {
    return null;
  }

  const selectedOption = question.option[selectedOptionIndex];

  const onChange = (event: ChangeEvent<{}>, value: number | number[]): void => {
    event.stopPropagation();
    event.preventDefault();
    if (Array.isArray(value)) {
      return;
    }
    const newOption = sortedOptions[value];
    newOption.id && onAnswerMemo(newOption.id, onAnswerInternalMemo);
  };

  // preloading all images and selectively hiding to make smooth transitions between slider options
  const optionImages = sortedOptions.map((option) => {
    const isSelectedOption = option.id === selectedOption.id;
    const imageClassName = clsx('Quiz-result_image', { hidden: !isSelectedOption });
    return (
      <OptionImage option={option} key={`Slider-option-${option.id}`} className={imageClassName} />
    );
  });

  const marks = sortedOptions.map((option, idx) => {
    return { label: option.displayText, value: idx };
  });

  return (
    <div>
      <div className="Quiz-sliderQuestion-options">
        <div className="Quiz-sliderQuestion-image">{optionImages}</div>
        <div className="Quiz-sliderQuestion-optionText">
          <Typography color="inherit" component="span" variant="heading03">
            {selectedOption && selectedOption.displayText}
          </Typography>
        </div>
        <Slider
          aria-label={question.displayText ?? 'question'}
          getAriaValueText={(value) => marks[value].label ?? `${value}`}
          min={0}
          max={question.option.length - 1}
          value={selectedOptionIndex}
          marks={marks}
          track={false}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onChange={onChange as any} // type error on library
        />
      </div>
    </div>
  );
};

export default SliderQuizQuestion;
